import { useMemo } from "react";
import { Redirect, Route, Router, Switch, useLocation } from "wouter";
import { SubjectRoles } from "./api-schema-enums";
import {
  collectRouteEntries,
  findBestMatchingNavigationItemByPath,
  findTopLevelNavigationItemByPath,
} from "./common/helpers/route";
import { useDocumentTitle } from "./common/hooks/use-document-title";
import { useRouteConfig } from "./common/hooks/use-navigation";
import { useExternalRootPath } from "./common/hooks/use-routes";
import { ErrorBoundary } from "./components/error-boundary/error-boundary";
import { RestrictedLayout } from "./components/layouts/restricted-layout";
import { Loader, LoaderSize } from "./components/ui/loader";
import { userStore } from "./store/user-store";
import { NotFoundView } from "./views/common/notfound.view";

export function Routes() {
  const [location] = useLocation();
  const { routeConfig, isLoading: isRouteConfigLoading } = useRouteConfig();
  const externalRootPath = useExternalRootPath(routeConfig);

  const user = userStore((state) => state.user);
  const isInternalUser = user?.roles.includes(SubjectRoles.Int) ?? false;
  const routeEntries = useMemo(() => collectRouteEntries(routeConfig, isInternalUser), [routeConfig, isInternalUser]);
  const activeNavigationItem = useMemo(
    () => findBestMatchingNavigationItemByPath(routeConfig, location),
    [location, routeConfig],
  );
  const moduleNavigationItem = useMemo(
    () => findTopLevelNavigationItemByPath(routeConfig, location),
    [location, routeConfig],
  );
  useDocumentTitle(location, activeNavigationItem, moduleNavigationItem, !!user && !isRouteConfigLoading);

  if (!user) return null;
  if (isRouteConfigLoading) return <Loader size={LoaderSize.Large} fullscreen />;

  return (
    <Router>
      <ErrorBoundary>
        <RestrictedLayout>
          <Switch>
            {!isInternalUser && (
              <>
                <Route path="/" component={() => <Redirect to={externalRootPath} replace />} />
                <Route path="/fit" component={() => <Redirect to={externalRootPath} replace />} />
                <Route path="/fit/" component={() => <Redirect to={externalRootPath} replace />} />
              </>
            )}
            {routeEntries.map((entry) => (
              <Route key={entry.path} path={entry.path} component={entry.component} nest={entry.nest} />
            ))}
            <Route component={NotFoundView} />
          </Switch>
        </RestrictedLayout>
      </ErrorBoundary>
    </Router>
  );
}
